import { i18nService } from '../../AppSettings/services/i18nService';
import Table from '../../Components/Table/Table';
import type { Column } from '../../Models/Table/TableModels';
import type { MicrosoftContact } from '../../types/team';

interface TeamContactsTabProps {
  loading: boolean;
  contacts: MicrosoftContact[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onAddContact: () => void;
}

const TeamContactsTab = ({
  loading,
  contacts,
  currentPage,
  totalPages,
  onPageChange,
  onAddContact,
}: TeamContactsTabProps): JSX.Element => {
  const columns: Column<MicrosoftContact>[] = [
    {
      header: i18nService.t('team.contacts.table.teamMembers.name'),
      accessor: 'displayName',
      render: (row) => {
        const fullName =
          row.givenName && row.surname
            ? `${row.givenName} ${row.surname}`
            : row.displayName || i18nService.t('generic.notApplicable');
        return fullName;
      },
    },
    {
      header: i18nService.t('team.contacts.table.teamMembers.email'),
      accessor: 'emailAddresses',
      render: (row) => {
        const email =
          row.emailAddresses && row.emailAddresses[0]
            ? row.emailAddresses[0].address
            : i18nService.t('generic.notApplicable');
        return <span style={{ color: '#6559F6' }}>{email}</span>;
      },
    },
    {
      header: i18nService.t('team.contacts.table.teamMembers.phone'),
      accessor: 'mobilePhone',
      render: (row) => {
        return (
          row.mobilePhone ||
          (row.businessPhones && row.businessPhones[0]) ||
          (row.homePhones && row.homePhones[0]) ||
          i18nService.t('generic.notApplicable')
        );
      },
    },
    {
      header: i18nService.t('team.contacts.table.teamMembers.company'),
      accessor: 'companyName',
      render: (row) => row.companyName || 'N/A',
    },
    {
      header: i18nService.t('team.contacts.table.teamMembers.jobTitle'),
      accessor: 'jobTitle',
      render: (row) => row.jobTitle || 'N/A',
    },
    {
      header: i18nService.t('team.contacts.table.teamMembers.actions'),
      render: () => (
        <button className="team-action-button">
          {i18nService.t('generic.buttons.actions')}
        </button>
      ),
    },
  ];

  if (loading) {
    return <div>{i18nService.t('team.contacts.loadingContacts')}</div>;
  }

  return (
    <>
      <div className="team-contacts-header">
        <button className="team-add-contact-button" onClick={onAddContact}>
          {i18nService.t('team.contacts.buttons.addNewContact')}
        </button>
      </div>

      <Table
        columns={columns}
        data={contacts}
        emptyMessage="No contacts found"
        rowKey="id"
      />

      <div className="team-pagination">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="team-pagination-button"
        >
          {i18nService.t('generic.buttons.previous')}
        </button>
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index + 1}
            onClick={() => onPageChange(index + 1)}
            className={`team-pagination-button ${
              currentPage === index + 1 ? 'active' : ''
            }`}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="team-pagination-button"
        >
          {i18nService.t('generic.buttons.next')}
        </button>
      </div>
    </>
  );
};

export default TeamContactsTab;
